import { Status, variantType } from "@/types";
import { TITLE_KEY, URLS } from "@/constants/constants";
import { VideoScript } from "@/app/(protected)/concepting/components/StepFour/types";
import { CopyContentItems } from "@/app/(protected)/pdp-copy/components/GeneratedCopy/components/CopyContent";
import { LONG_BULLETS, SHORT_BULLETS } from "@/app/(protected)/pdp-copy/components/GeneratedCopy/constants";
import { cleanString } from "./string.utils";
import { isConversationalEndpoint } from "./common";

import successIcon from "../../icons/success/success.svg";
import checkSuccessIcon from "../../icons/check/check-success.svg";
import checkSuccessYellowIcon from "../../icons/success/success-yellow.svg";
import errorIconBold from "../../icons/info/info-message-bold.svg";
import triangleWarningIcon from "../../icons/caution/caution.svg";
import errorIcon from "../../icons/info/Group-red 1010107571.svg";

const { ACTIVE, COMPLETED, DISABLED } = Status;

export const getBackgroundColor = (status: "active" | "completed" | "disabled") => {
  switch (status) {
    case ACTIVE:
      return "bg-mondelez-primary-purple";
    case COMPLETED:
      return "bg-success";
    case DISABLED:
      return "bg-white";
    default:
      return "bg-mondelez-tints-purple";
  }
};

export const getTextColor = (status: "active" | "completed" | "disabled") => {
  switch (status) {
    case ACTIVE:
      return "text-white";
    case COMPLETED:
      return "text-white";
    case DISABLED:
      return "text-mondelez-primary-purple";
    default:
      return "text-mondelez-primary-purple";
  }
};

export const getBorderColor = (status?: "error" | "duplicate") => {
  switch (status) {
    case "error":
      return "border-error";
    case "duplicate":
      return "border-success";
    default:
      return "border-mondelez-tints-purple-20";
  }
};

export const getVariant = (variant: variantType) => {
  switch (variant) {
    case "success":
      return successIcon;
    case "checkSuccess":
      return checkSuccessIcon;
    case "warning":
      return checkSuccessYellowIcon;
    case "error-high":
      return errorIconBold;
    case "caution":
      return triangleWarningIcon;
    default:
      return errorIcon;
  }
};

export const formatHeadlineText = (content: string[]) => {
  let formattedText = "";
  content?.forEach((e, i) => {
    const title = "Heading " + (i + 1);
    formattedText += "Title: " + title + "\nDescription: " + e + "\n\n";
  });
  return formattedText;
};

export const getFormattedPdpCopyText = (items: CopyContentItems[], isDownload = false) =>
  items
    .map((item) => {
      const isBullets = item.label === LONG_BULLETS || item.label === SHORT_BULLETS;
      let description = item.description;

      if (Array.isArray(description)) {
        description = description
          .map((line) => {
            const cleaned = line.trim();
            return isDownload && isBullets && cleaned.endsWith(".") ? cleaned.slice(0, -1) : cleaned;
          })
          .join(",");
      } else if (typeof description === "string" && isDownload && isBullets) {
        const cleaned = description.trim();
        if (cleaned.endsWith(".")) {
          description = cleaned.slice(0, -1);
        }
      }

      return `Title: ${item.label}\nDescription: ${description}\n\n`;
    })
    .join("");

export const getScriptFormattedText = (videoScript?: VideoScript, descriptionData?: { title: string; description: string }) => {
  let text: string = "";

  const processObject = (obj: object) => {
    const entries = Object.entries(obj as Record<string, unknown>);
    const titleEntry = entries.filter(([key]) => key === TITLE_KEY);
    const otherEntries = entries.filter(([key]) => key !== TITLE_KEY);
    [...titleEntry, ...otherEntries].forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === "object" && item !== null) {
            text += `\n`;
            processObject(item);
          } else {
            text += `${cleanString(key.charAt(0).toUpperCase() + key.slice(1))}: ${item}\n`;
          }
        });
      } else if (typeof value === "object" && value !== null) {
        processObject(value);
      } else {
        text += `${cleanString(key.charAt(0).toUpperCase() + key.slice(1))}: ${value}\n`;
      }
    });
  };

  if (descriptionData) {
    processObject(descriptionData);
    text += "\n";
  }

  if (videoScript) {
    processObject(videoScript);
  }

  return text;
};

export const getPaginationText = (currentPage: number, totalPage: number, totalItems: number) => {
  return `Page ${currentPage} of ${totalPage} loaded, showing ${totalItems} results`;
};

export const isPermanentSidebarRoute = (pathname: string) => {
  const firstSegment = pathname.split("/").find(Boolean) ?? "";
  return pathname === URLS.HOME || isConversationalEndpoint(firstSegment);
};

export const hideSideBarToggle = (pathname: string, showAdminSidebarOptions: boolean = false) => {
  const baseUrls = [URLS.HOME, URLS.TERMS_OF_SERVICE, URLS.ACCESSIBILITY, URLS.CONTACT_US, URLS.RESPONSIBLE_AI, URLS.PRIVACY_POLICY];
  const urls = showAdminSidebarOptions ? baseUrls : [...baseUrls, URLS.RAISE_REQUEST, URLS.MANAGE_USERS];
  return urls.includes(pathname) || isPermanentSidebarRoute(pathname);
};
